import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Eye, Download, MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { Mask } from "@/utils/mask";
import { WalletOperation, WalletOperationsResponse } from "../shared/types";
import { useState, useMemo } from "react";

type SortOrder = "asc" | "desc";

interface TableDataProps {
  dataApi: WalletOperationsResponse;
  pageActions: {
    next: () => void;
    previous: () => void;
    lastPage: () => void;
  };
}

export const TableData = ({ dataApi, pageActions }: TableDataProps) => {
  const [sortField, setSortField] = useState<keyof WalletOperation | "">("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [viewModal, setViewModal] = useState<{
    isOpen: boolean;
    item: WalletOperation | null;
  }>({
    isOpen: false,
    item: null,
  });

  const handleSort = (field: keyof WalletOperation) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!dataApi?.data || !sortField) return dataApi?.data || [];

    return [...dataApi.data].sort((a, b) => {
      const valueA = a[sortField] || "";
      const valueB = b[sortField] || "";

      if (valueA < valueB) return sortOrder === "asc" ? -1 : 1;
      if (valueA > valueB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [dataApi?.data, sortField, sortOrder]);

  const renderSortIndicator = (field: keyof WalletOperation) => {
    if (sortField !== field) return null;
    return sortOrder === "asc" ? " ▲" : " ▼";
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const handleDownload = (fileUrl: string) => {
    window.open(fileUrl, "_blank");
  };

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead
              className="cursor-pointer"
              onClick={() => handleSort("client_name")}
            >
              Cliente{renderSortIndicator("client_name")}
            </TableHead>
            <TableHead>Vendedor</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead className="text-right">Valor</TableHead>
            <TableHead>Gerado por</TableHead>
            <TableHead
              className="cursor-pointer"
              onClick={() => handleSort("created_at")}
            >
              Data/Hora{renderSortIndicator("created_at")}
            </TableHead>
            <TableHead className="text-center">Arquivo</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedData && sortedData.length > 0 ? (
            sortedData.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{item.client_name}</TableCell>
                <TableCell>{item.seller_name || "—"}</TableCell>
                <TableCell>
                  <span
                    className={cn(
                      "px-2 py-1 rounded text-xs font-semibold",
                      item.type === "addition"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    )}
                  >
                    {item.type === "addition" ? "ADIÇÃO" : "SUBTRAÇÃO"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <span
                    className={cn(
                      "font-semibold px-2 py-1 rounded",
                      item.type === "addition"
                        ? "bg-green-50 text-green-600"
                        : "bg-red-50 text-red-600"
                    )}
                  >
                    {item.type === "subtraction" && "- "}
                    {Mask.formatCurrency(Math.abs(item.amount))}
                  </span>
                </TableCell>
                <TableCell>{item.created_by_name}</TableCell>
                <TableCell>{formatDateTime(item.created_at)}</TableCell>
                <TableCell className="text-center">
                  {item.file_url ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 p-0 text-blue-600 hover:bg-blue-50"
                      onClick={() => handleDownload(item.file_url!)}
                      title="Baixar arquivo"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
                    onClick={() => setViewModal({ isOpen: true, item })}
                    title="Ver descrição"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={8} className="text-center py-8">
                Nenhuma operação encontrada.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {dataApi?.last_page > 1 && (
        <div className="flex justify-between mb-3 mt-4">
          {dataApi?.data && dataApi?.data.length > 0 && (
            <span className="font-medium">
              Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
            </span>
          )}
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <Dialog
        open={viewModal.isOpen}
        onOpenChange={(open) => !open && setViewModal({ isOpen: false, item: null })}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Detalhes da Operação</DialogTitle>
          </DialogHeader>
          {viewModal.item && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Cliente</label>
                  <p className="font-semibold">{viewModal.item.client_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Vendedor</label>
                  <p>{viewModal.item.seller_name || "—"}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Tipo</label>
                  <p>
                    <span
                      className={cn(
                        "px-2 py-1 rounded text-xs font-semibold",
                        viewModal.item.type === "addition"
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      )}
                    >
                      {viewModal.item.type === "addition" ? "ADIÇÃO" : "SUBTRAÇÃO"}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Valor</label>
                  <p
                    className={cn(
                      "font-bold text-lg",
                      viewModal.item.type === "addition"
                        ? "text-green-600"
                        : "text-red-600"
                    )}
                  >
                    {viewModal.item.type === "subtraction" && "- "}
                    {Mask.formatCurrency(Math.abs(viewModal.item.amount))}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Gerado por</label>
                  <p>{viewModal.item.created_by_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Data/Hora</label>
                  <p>{formatDateTime(viewModal.item.created_at)}</p>
                </div>
              </div>

              <div className="pt-4 border-t">
                <label className="text-sm font-medium text-gray-500">Descrição</label>
                <p className="mt-1 text-gray-700 whitespace-pre-wrap">
                  {viewModal.item.description || "Nenhuma descrição informada."}
                </p>
              </div>

              {viewModal.item.file_url && (
                <div className="pt-4 border-t">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => handleDownload(viewModal.item!.file_url!)}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Baixar Arquivo Anexo
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
